// Generated by GitHub Copilot
import { motion } from "framer-motion";
import LoadingFallback from "./LoadingFallback";

const FALLBACK_MEDIUM_TOPICS = [
    {
        title: "Applied AI System Design Notes",
        excerpt: "Practical observations on shipping ML and GenAI systems with production constraints.",
    },
    {
        title: "LLM Reliability and Evaluation",
        excerpt: "Approaches for faithfulness checks, evaluation loops, and guardrail-oriented delivery.",
    },
    {
        title: "MLOps and Observability Patterns",
        excerpt: "Hands-on monitoring, drift awareness, and incident response practices for AI workloads.",
    },
    {
        title: "Frontend for Data and AI Products",
        excerpt: "Implementation notes from building responsive interfaces around model-backed experiences.",
    },
    {
        title: "Cloud and Deployment Learnings",
        excerpt: "Operational lessons from deploying analytics and AI products to cloud-native stacks.",
    },
];

/**
 * Return medium cards with live entries when available and deterministic fallback cards otherwise.
 *
 * @param {Record<string, any>} profile Profile payload.
 * @param {string} profileUrl Medium profile URL.
 * @returns {Array<Record<string, any>>} Visible article card entries.
 */
function getVisibleMediumArticles(profile, profileUrl) {
    const liveArticles = Array.isArray(profile?.medium?.articles) ? profile.medium.articles : [];
    if (liveArticles.length) {
        return liveArticles.slice(0, 5);
    }

    const configuredFallback = Array.isArray(profile?.medium?.fallbackArticles)
        ? profile.medium.fallbackArticles
            .map((article, index) => ({
                id: article.id || `fallback-configured-${index}`,
                title: article.title || "Medium article",
                link: article.link || profileUrl,
                excerpt: article.excerpt || "Open profile to read this article.",
            }))
            .slice(0, 5)
        : [];

    if (configuredFallback.length) {
        return configuredFallback;
    }

    return FALLBACK_MEDIUM_TOPICS.map((topic, index) => ({
        id: `fallback-topic-${index}`,
        title: topic.title,
        link: profileUrl,
        excerpt: topic.excerpt,
    }));
}

/**
 * Render Medium highlights and certification tags as a scroll-revealed insight block.
 *
 * @param {{profile: Record<string, any>, loading?: boolean}} props Component props.
 * @returns {JSX.Element} Insights section markup.
 */
function Insights({ profile, loading = false }) {
    const mediumProfileUrl = profile?.medium?.profileUrl || "https://medium.com/@RamPrakashD";
    const visibleMediumArticles = getVisibleMediumArticles(profile, mediumProfileUrl);
    const hasLiveArticles = Boolean(profile?.medium?.articles?.length);
    const certifications = profile?.certifications || [];

    return (
        <section className="py-16" id="insights">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.2, once: true }}
                transition={{ duration: 0.6, ease: "easeOut" }}
            >
                <h2 className="text-3xl font-bold">Medium Insights and Certifications</h2>
                <p className="mt-2 max-w-3xl text-[var(--text-secondary)]">
                    Live writing from Medium and credential signals that reflect ongoing experimentation in
                    applied AI, MLOps, and product engineering.
                </p>
            </motion.div>

            <div className="mt-8 grid gap-6 lg:grid-cols-[2fr,1fr]">
                <motion.div
                    className="glass-card rounded-3xl p-6"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.2, once: true }}
                    transition={{ delay: 0.1, duration: 0.6, ease: "easeOut" }}
                >
                    <div className="mb-4 flex items-center justify-between gap-3">
                        <h3 className="text-xl font-semibold">Latest Medium Articles</h3>
                        <a
                            href={mediumProfileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm font-semibold text-[rgba(var(--accent-rgb),0.95)] hover:underline"
                        >
                            Visit profile
                        </a>
                    </div>

                    {!hasLiveArticles && !loading ? (
                        <p className="mb-3 text-xs uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                            Live Medium feed unavailable in this environment. Showing profile highlights.
                        </p>
                    ) : null}

                    {loading ? (
                        <LoadingFallback
                            label="Loading medium insights"
                            headingWidth={220}
                            subheadingWidth={160}
                            cardCount={2}
                            rowCount={4}
                        />
                    ) : (
                        <ul className="space-y-3">
                            {visibleMediumArticles.map((article) => (
                                <li
                                    key={article.id}
                                    className="glass-card-soft rounded-2xl p-4"
                                >
                                    <a
                                        href={article.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-base font-semibold text-[var(--text-primary)] hover:text-[rgba(var(--accent-rgb),0.95)]"
                                    >
                                        {article.title}
                                    </a>
                                    <p className="mt-2 text-sm text-[var(--text-secondary)]">{article.excerpt}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </motion.div>

                <motion.aside
                    className="glass-card rounded-3xl p-6"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.2, once: true }}
                    transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
                >
                    <h3 className="text-xl font-semibold">Certifications</h3>
                    {loading ? (
                        <div className="mt-4 grid gap-2" role="status" aria-live="polite" aria-label="Loading certifications" aria-busy="true">
                            <div className="skeleton" style={{ height: 32 }} />
                            <div className="skeleton" style={{ height: 32 }} />
                            <div className="skeleton" style={{ height: 32 }} />
                        </div>
                    ) : (
                        <ul className="mt-4 space-y-2">
                            {certifications.map((certification) => (
                                <li
                                    key={certification}
                                    className="glass-card-soft rounded-xl px-3 py-2 text-sm"
                                >
                                    {certification}
                                </li>
                            ))}
                        </ul>
                    )}
                </motion.aside>
            </div>
        </section>
    );
}

export default Insights;
